import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { orderAPI, itemAPI } from '../services/api';

const ITEM_TYPES = ['Shoe', 'Bag', 'Suitcase', 'Other'];
const BASE_DAYS_MAP = { Shoe: 2, Bag: 3, Suitcase: 4, Other: 3 };

const OrderEntry = () => {
  const { selectedShop } = useOutletContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [orderId, setOrderId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const [items, setItems] = useState([
    { item_type: 'Shoe', issue_description: '', estimated_price: '', actual_price: '' }
  ]);
  const [removedItemIds, setRemovedItemIds] = useState([]);

  // Fetch existing order when in edit mode
  useEffect(() => {
    if (isEditing) {
      fetchOrderForEdit();
    }
    // eslint-disable-next-line
  }, [id]);

  const fetchOrderForEdit = async () => {
    try {
      setInitialLoading(true);
      setError(null);
      const res = await orderAPI.getById(id, selectedShop);
      const order = res.data;

      setOrderId(order.order_id);
      setCustomerName(order.customer_name);
      setPhoneNumber(order.phone_number);
      setPriority(order.priority);
      setLocation(order.customer_location || '');
      setNotes(order.notes || '');
      setTargetDate(order.target_date);
      setItems(
        (order.items || []).map((i) => ({
          item_id: i.item_id,
          item_type: i.item_type,
          issue_description: i.issue_description,
          estimated_price: i.estimated_price !== null && i.estimated_price !== undefined ? i.estimated_price : '',
          actual_price: i.actual_price !== null && i.actual_price !== undefined ? i.actual_price : ''
        }))
      );
    } catch (err) {
      setError('Failed to load order for editing.');
    } finally {
      setInitialLoading(false);
    }
  };

  const addItemRow = () => {
    setItems([...items, { item_type: 'Shoe', issue_description: '', estimated_price: '', actual_price: '' }]);
  };

  const removeItemRow = (indexToRemove) => {
    if (items.length <= 1) return;
    const itemToRemove = items[indexToRemove];
    // Track existing items removed during edit so we can delete them on the backend
    if (itemToRemove.item_id) {
      setRemovedItemIds((prev) => [...prev, itemToRemove.item_id]);
    }
    setItems(items.filter((_, idx) => idx !== indexToRemove));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const fetchSuggestedDate = async () => {
    try {
      setError(null);
      const typeToQuery = items.reduce((best, item) => {
        return (BASE_DAYS_MAP[item.item_type] || 0) > (BASE_DAYS_MAP[best] || 0)
          ? item.item_type : best;
      }, items[0]?.item_type || 'Shoe');

      const res = await orderAPI.suggestTargetDate(typeToQuery, priority, new Date().toLocaleDateString('en-CA'));
      setTargetDate(res.data.suggested_target_date);
    } catch (err) {
      console.error("Failed to suggest target date", err);
    }
  };

  useEffect(() => {
    if (!isEditing) fetchSuggestedDate();
    // eslint-disable-next-line
  }, [priority, items.map(i => i.item_type).join(',')]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!isEditing && !orderId.trim()) return setError('Order ID is required');
    if (!customerName.trim()) return setError('Customer Name is required');
    if (!phoneNumber.trim()) return setError('Phone Number is required');
    if (items.some(i => !i.issue_description.trim())) return setError('All items must have an issue description');

    try {
      setLoading(true);

      if (isEditing) {
        // 1. Update order header fields (PUT /orders/{id})
        await orderAPI.update(id, selectedShop, {
          customer_name: customerName.trim(),
          phone_number: phoneNumber.trim(),
          customer_location: location.trim() || null,
          notes: notes.trim() || null,
          priority: priority,
          target_date: targetDate || null
        });

        // 2. Update existing items, or add newly added rows
        for (const item of items) {
          const itemPayload = {
            item_type: item.item_type,
            issue_description: item.issue_description.trim(),
            estimated_price: item.estimated_price !== '' ? Number(item.estimated_price) : null,
            actual_price: item.actual_price !== '' ? Number(item.actual_price) : null
          };

          if (item.item_id) {
            await itemAPI.updateItem(id, item.item_id, selectedShop, itemPayload);
          } else {
            await itemAPI.addItem(id, selectedShop, itemPayload);
          }
        }

        // 3. Delete items removed during this edit session
        for (const removedId of removedItemIds) {
          await itemAPI.removeItem(id, removedId, selectedShop);
        }

        navigate('/worker-view');

      } else {
        // Original create flow — unchanged
        const payload = {
          order_id: orderId.trim(),
          customer_name: customerName.trim(),
          phone_number: phoneNumber.trim(),
          shop: selectedShop,
          priority: priority,
          customer_location: location.trim() || null,
          notes: notes.trim() || null,
          target_date: targetDate || null,
          items: items.map((item, index) => ({
            item_sequence: index + 1,
            item_type: item.item_type,
            issue_description: item.issue_description.trim(),
            estimated_price: item.estimated_price !== '' ? Number(item.estimated_price) : null
          }))
        };

        await orderAPI.create(payload);
        navigate('/worker-view');
      }
    } catch (err) {
      const serverMsg = err.response?.data?.detail || 'Failed to save order. Check if Order ID already exists.';
      setError(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-12">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">
          {isEditing ? `Edit Order #${orderId}` : 'Create New Order'}
        </h2>
        <span className="bg-blue-100 text-blue-800 text-sm font-bold px-3 py-1 rounded-full">
          {selectedShop} Branch
        </span>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 border-b border-gray-100 pb-3 mb-4">Customer Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Order ID *</label>
              <input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value.toUpperCase())}
                required={!isEditing}
                disabled={isEditing}
                placeholder="e.g. NL001"
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 uppercase disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Priority *</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input type="radio" value="Normal" checked={priority === 'Normal'} onChange={(e) => setPriority(e.target.value)} className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                  <span className="ml-2 text-sm text-gray-700 font-medium">Normal</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" value="Urgent" checked={priority === 'Urgent'} onChange={(e) => setPriority(e.target.value)} className="h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500" />
                  <span className="ml-2 text-sm text-red-600 font-bold">Urgent</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Customer Name *</label>
              <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number *</label>
              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-1">Target Date *</label>
              <div className="flex items-center space-x-2">
                <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} required className="w-full md:w-1/3 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                <button type="button" onClick={fetchSuggestedDate} className="text-sm font-semibold text-blue-600 hover:text-blue-800 underline">
                  Auto-suggest
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-1">Location (Optional)</label>
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-1">Internal Notes (Optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2" className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"></textarea>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex justify-between items-center border-b border-gray-100 pb-3 mb-4">
            <h3 className="text-lg font-bold text-gray-800">Order Items</h3>
            <button type="button" onClick={addItemRow} className="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-md text-sm font-bold transition-colors">
              + Add Item
            </button>
          </div>

          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={item.item_id || `new-${index}`} className="relative bg-gray-50 p-4 rounded-lg border border-gray-200">

                {items.length > 1 && (
                  <button type="button" onClick={() => removeItemRow(index)} className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm font-bold">
                    ✕ Remove
                  </button>
                )}

                <h4 className="font-bold text-gray-700 mb-3">Item #{index + 1}</h4>

                <div className={`grid grid-cols-1 gap-4 ${isEditing ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
                  <div className="md:col-span-1">
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Type *</label>
                    <select value={item.item_type} onChange={(e) => updateItem(index, 'item_type', e.target.value)} className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500">
                      {ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Issue *</label>
                    <input type="text" value={item.issue_description} onChange={(e) => updateItem(index, 'issue_description', e.target.value)} required placeholder="Describe the repair..." className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                  </div>

                  <div className="md:col-span-1">
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Est. Price (₹)</label>
                    <input type="number" min="0" value={item.estimated_price} onChange={(e) => updateItem(index, 'estimated_price', e.target.value)} placeholder="Optional" className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                  </div>

                  {isEditing && (
                    <div className="md:col-span-1">
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Actual Price (₹)</label>
                      <input type="number" min="0" value={item.actual_price} onChange={(e) => updateItem(index, 'actual_price', e.target.value)} placeholder="Optional" className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 rounded-xl text-lg font-black text-white shadow-md transition-colors ${loading ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
          >
            {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Order')}
          </button>
        </div>

      </form>
    </div>
  );
};

export default OrderEntry;